import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { PatchProgramsIdMutationRequest } from '@eureca/recruiter-api-sdk/types';
import { withNormalizedApiDates } from '@/lib/api-dates';

export interface ProgramCreateBasePayload {
  companyId: string;
  statusId: number;
  name: string;
  description?: string | null;
  startApplying?: string | null;
  endApplying?: string | null;
  publishedAt?: string | null;
}

export interface ProgramCreated {
  id: string;
  companyId: string;
  statusId: number;
  name: string;
  description: string | null;
  startApplying: string | null;
  endApplying: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

/**
 * Full create-flow payload: the Core POST only accepts base fields, but the
 * consultor fills 6 extras in Steps 1/2/3 that the form would silently drop
 * without the post-create PATCH (workCondition + collections + faqs) and the
 * optional cover upload. `coverFile` is null when the consultor skipped the
 * cover; an undefined value would mean the same, but the explicit `null`
 * documents intent.
 */
export interface ProgramFullCreateInput {
  base: ProgramCreateBasePayload;
  /** Skipped entirely when every field is null/empty. */
  patch: PatchProgramsIdMutationRequest | null;
  coverFile: File | null;
  /**
   * EU-641 (FQC-014) — FAQs padrão que o consultor OCULTOU durante a criação
   * (escolhas em memória até aqui). Vazio = nenhum PUT (todo programa nasce
   * com o conjunto completo ligado).
   */
  hiddenDefaultFaqIds?: number[];
}

export class ProgramCreateError extends Error {
  readonly name = 'ProgramCreateError';
  /**
   * Phase the request failed at, so the UI can decide whether the program
   * exists in a partial state and the consultor can be sent to the edit page
   * to complete it (`patch`/`cover`) or whether nothing was created (`post`).
   */
  readonly phase: 'post' | 'patch' | 'cover' | 'default-faqs';
  /** Set once the base POST succeeds — lets callers reach the edit page. */
  readonly programId: string | null;
  /**
   * Domain code the BFF emits for typed 4xx responses. Today the only
   * value is `'duplicate_program_name'` (RN-03 server-side gate). Lets the
   * SPA render the AC-literal message inline without re-mapping HTTP codes.
   */
  readonly code: 'duplicate_program_name' | null;
  constructor(
    message: string,
    readonly status: number,
    phase: 'post' | 'patch' | 'cover' | 'default-faqs' = 'post',
    programId: string | null = null,
    code: 'duplicate_program_name' | null = null
  ) {
    super(message);
    this.phase = phase;
    this.programId = programId;
    this.code = code;
  }
}

/** Narrow a fetchClient error body into the typed code, when present. */
function extractErrorCode(errBody: unknown): 'duplicate_program_name' | null {
  if (errBody && typeof errBody === 'object' && 'error' in errBody) {
    const value = (errBody as { error?: unknown }).error;
    if (value === 'duplicate_program_name') return 'duplicate_program_name';
  }
  return null;
}

/**
 * Orchestrates the 3 calls Core requires to fully save what the form collects:
 *
 *   1. POST /programs                  — base row, returns the program id.
 *   2. PATCH /programs/:id             — workCondition + benefits + payments +
 *                                        FAQs (only when there's something to
 *                                        write; an empty patch is skipped).
 *   3. POST /programs/:id/cover        — multipart, only when the consultor
 *                                        actually picked a file.
 *
 * Failure at step 2 or 3 leaves a Rascunho program with partial data. That's
 * acceptable UX for a draft (the consultor can reopen via edit and complete),
 * and surfaces via `ProgramCreateError.phase` so the caller can toast the
 * right message and reuse `programId` for a follow-up edit nav.
 */
export async function createProgramFull(input: ProgramFullCreateInput): Promise<ProgramCreated> {
  const postRes = await fetchClient<ProgramCreated>({
    method: 'POST',
    url: '/programs',
    // EU-402 RN-01/RN-03 — normalize startApplying/endApplying/publishedAt to
    // ISO 8601 before sending (the draft may carry hydrated instants).
    data: withNormalizedApiDates(input.base),
  });
  if (postRes.error) {
    throw new ProgramCreateError(
      `Falha ao criar programa (status ${postRes.error.status})`,
      postRes.error.status,
      'post',
      null,
      extractErrorCode(postRes.error.error)
    );
  }
  const program = postRes.data;

  if (input.patch && hasAnyPatchField(input.patch)) {
    const patchRes = await fetchClient({
      method: 'PATCH',
      url: `/programs/${program.id}`,
      data: withNormalizedApiDates(input.patch),
    });
    if (patchRes.error) {
      throw new ProgramCreateError(
        `Falha ao salvar detalhes do programa (status ${patchRes.error.status})`,
        patchRes.error.status,
        'patch',
        program.id
      );
    }
  }

  if (input.coverFile) {
    const formData = new FormData();
    formData.append('file', input.coverFile, input.coverFile.name);
    const coverRes = await fetchClient({
      method: 'POST',
      url: `/programs/${program.id}/cover`,
      data: formData,
    });
    if (coverRes.error) {
      throw new ProgramCreateError(
        `Falha ao enviar capa (status ${coverRes.error.status})`,
        coverRes.error.status,
        'cover',
        program.id
      );
    }
  }

  // EU-641 (FQC-014) — aplica o conjunto oculto escolhido durante a criação.
  // Só roda quando há oculta (default = conjunto completo ligado, sem PUT).
  if (input.hiddenDefaultFaqIds && input.hiddenDefaultFaqIds.length > 0) {
    const faqsRes = await fetchClient({
      method: 'PUT',
      url: `/programs/${program.id}/hidden-default-faqs`,
      data: { faqIds: input.hiddenDefaultFaqIds },
    });
    if (faqsRes.error) {
      throw new ProgramCreateError(
        `Falha ao aplicar as FAQs padrão (status ${faqsRes.error.status})`,
        faqsRes.error.status,
        'default-faqs',
        program.id
      );
    }
  }

  return program;
}

function hasAnyPatchField(patch: PatchProgramsIdMutationRequest): boolean {
  const hasWorkCondition = Boolean(
    patch.workCondition &&
    (patch.workCondition.contractTypeKey != null || patch.workCondition.subscriberTarget != null)
  );
  return (
    hasWorkCondition ||
    Boolean(patch.benefitNames && patch.benefitNames.length > 0) ||
    Boolean(patch.additionalPaymentNames && patch.additionalPaymentNames.length > 0) ||
    Boolean(patch.faqs && patch.faqs.length > 0)
  );
}
